// ------------------------------------------------------------------------------
// PreviewImage - 主要用于展现预览图（一般用于上传图片场景，暂不支持展示视频）
// author: mudas
// created: 2025/2/13
// ------------------------------------------------------------------------------

import React, { FC } from 'react';
import { isNil } from 'lodash-es';
import { ZoomInOutlined } from '@ant-design/icons';
import { useControllableValue } from 'ahooks';
import { useComputed } from 'common/hooks';

import { EyeOutlined } from '@ant-design/icons';
import {
  DraggerUploadFile,
  SocialArtworkStatusEnum,
  SocialArtworkStatusTag,
  SocialArtworkStatusTagColor,
} from 'common/types';
import { Button, Checkbox, Flex, Icon, Image, Popconfirm, Progress, Spin, Tooltip, Typography } from '@fuxi/eevee-ui';

// import DefaultImage from 'common/assets/image/default-card-img.webp';
import IllegalPNG from 'common/assets/image/illegal.png';
import cx from './PreviewImage.module.css';

export type SelectedType = { uid: string; value: boolean };

export interface IProps {
  // 唯一标识：触发 onDelete 时作为参数返回
  uid: string;

  // 图片地址
  url: string;

  artWorkStatus?: SocialArtworkStatusEnum;

  // 违禁图片标识
  illegal?: boolean;
  showLabel?: boolean;

  // 上传进度展示（取值范围：0-100）
  percent?: number;
  status?: DraggerUploadFile['status'];

  // 点击删除按钮时触发（未设置该回调方法则不显示删除按钮）
  onDelete?: (uid: string) => void;
  showDeleteConfirm?: boolean; // 删除时是否显示气泡确认提示

  // 点击选定当前图片（未设置该回调方法则不显示选择框）
  selected?: SelectedType;
  onSelect?: (value: SelectedType) => void;

  // 点击预览
  onPreview?: (uid: string) => void;

  onBatchModePreview?: (uid: string) => void;
  onImportFineWork?: (uid: string) => void;
}

/**
 * 预览图片容器组件
 * - 特点：宽度随父级决定，高度自动适应宽度，呈现 1:1 正方形容器来展示图片
 * @param props
 * @constructor
 */
const PreviewImage: FC<IProps> = (props) => {
  const {
    uid,
    url,
    showLabel = true,
    showDeleteConfirm = true,
    percent,
    status,
    illegal,
    onDelete,
    onSelect,
    onPreview,
    artWorkStatus,
    onImportFineWork,
    onBatchModePreview,
  } = props;

  const [selected, setSelected] = useControllableValue<SelectedType>(props, {
    valuePropName: 'selected',
    trigger: 'onSelect',
    defaultValue: {} as SelectedType,
  });

  // 若设置了上传状态，则计算是否上传完成
  const ready = useComputed<boolean>(() => {
    if (isNil(percent) && isNil(status)) return true;
    return percent === 100 && status === 'done';
  }, [percent, status]);

  const allowSelectAtHasHolder = () => {
    if (!!onSelect) setSelected({ uid, value: !selected.value });
  };

  const handlePreview = (e) => {
    e.stopPropagation();
    onPreview?.(uid);
  };

  const handleBatchModePreview = (e) => {
    e.stopPropagation();
    onBatchModePreview?.(uid);
  };

  return (
    <div
      className={cx('preview-wrapper', {
        selected: selected.value,
        previewed: !!onPreview,
        illegal: illegal,
        'select-openned': !!onSelect,
      })}
      onClick={allowSelectAtHasHolder}>
      <div hidden={!onBatchModePreview} className={cx('batch-mode-preview')}>
        <ZoomInOutlined className="font-s-lg" onClick={handleBatchModePreview} />
      </div>

      <div
        hidden={isNil(artWorkStatus) || !showLabel}
        className={`pos-absolute top-8px left-0 w-52px h-22px z-10 flex`}
        style={{ backgroundColor: SocialArtworkStatusTagColor[artWorkStatus!], borderRadius: '0px 4px 4px 0px' }}>
        <Typography.Text className="font-s-sm m-a">{SocialArtworkStatusTag[artWorkStatus!]}</Typography.Text>
      </div>
      <div className={cx('img-wrapper')}>
        {ready ? (
          <Image
            src={url}
            preview={false}
            wrapperClassName={cx('square-image')}
            // placeholder={<Image src={DefaultImage} preview={false} />}
            placeholder={
              <div className="w-full h-full flex-center">
                <Spin />
              </div>
            }
          />
        ) : (
          <div className={cx('square-image', 'flex-center text-color-tertiary b-1 b-solid color-border-normal')}>
            <Progress
              type="circle"
              size={60}
              percent={percent}
              className="opacity-60"
              format={(percent) => <span className="text-size-xs">上传中</span>}
            />
          </div>
        )}
      </div>

      {illegal && <Image wrapperClassName={cx('illegal-image')} preview={false} src={IllegalPNG} />}

      <div className={cx('header-shadow')}></div>

      <div className={cx('mask-content-wrapper')} onClick={(e) => !!onPreview && handlePreview(e)}>
        <Flex flexDirection="column" gapXXS alignCenter className={cx('mask-content')}>
          <Flex.Item>
            <EyeOutlined />
            <Typography.Text>查看大图</Typography.Text>
          </Flex.Item>

          <Button
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              onImportFineWork?.(uid);
            }}
            hidden={!onImportFineWork || artWorkStatus !== SocialArtworkStatusEnum.NonFineArtwork}>
            移入美术作品
          </Button>
        </Flex>
      </div>

      <div className={cx('icon-wrapper')}>
        {!!onDelete &&
          (showDeleteConfirm ? (
            <Popconfirm title="确认要删除该张作品吗？" onConfirm={() => onDelete?.(uid)}>
              <Icon size={16} name="delete-filled" className={cx('icon-delete')} />
            </Popconfirm>
          ) : (
            <Icon size={16} name="delete-filled" className={cx('icon-delete')} onClick={() => onDelete?.(uid)} />
          ))}

        {!!onSelect && (
          <Checkbox
            checked={selected.value}
            className={cx('checkbox', { selected: selected.value })}
            onChange={(e) => setSelected({ uid, value: e.target.checked })}
          />
        )}
      </div>
    </div>
  );
};

export default PreviewImage;
